import { useMemo, useState, useEffect } from "react";
import { FiChevronDown, FiFilter, FiSearch, FiSliders, FiX } from "react-icons/fi";
import ReactPaginate from "react-paginate";
import Card from "../../../components/Card/Card";
import ButtonRound from "../../../components/Labels/ButtonRound";
import { useProducts } from "../../../hooks/useProducts";
import { Product } from "../../../api-client";

type SortOption = "newest" | "price-low" | "price-high" | "name";

const ProductsFilterPanel = ({
    categories,
    selectedCategory,
    onSelectCategory,
    colors,
    selectedColors,
    onToggleColor,
    sizes,
    selectedSizes,
    onToggleSize,
    minPrice,
    maxPrice,
    onMinPriceChange,
    onMaxPriceChange,
    onClear,
}: {
    categories: string[];
    selectedCategory: string;
    onSelectCategory: (category: string) => void;
    colors: Array<{ name: string; hex: string }>;
    selectedColors: string[];
    onToggleColor: (colorName: string) => void;
    sizes: string[];
    selectedSizes: string[];
    onToggleSize: (size: string) => void;
    minPrice: string;
    maxPrice: string;
    onMinPriceChange: (value: string) => void;
    onMaxPriceChange: (value: string) => void;
    onClear: () => void;
}) => {
    return (
        <aside className="bg-[#F2F2F3] rounded-2xl border border-[#E6E7EA] p-5">
            <div className="flex items-center justify-between border-b border-[#DCDDDF] pb-4 mb-5">
                <div className="flex items-center gap-2">
                    <FiSliders className="text-lg text-[#22232A]" />
                    <h3 className="text-3xl md:text-2xl font-semibold text-[#21232A]">Filters</h3>
                </div>
                <button
                    onClick={onClear}
                    className="text-[#FF4E1A] font-semibold hover:opacity-80 transition-opacity"
                >
                    Clear Filters
                </button>
            </div>

            <div className="space-y-7">
                <section>
                    <h4 className="text-[#21232A] text-3xl md:text-xl font-semibold mb-3">Categories</h4>
                    <div className="space-y-2">
                        <button
                            onClick={() => onSelectCategory("")}
                            className={`w-full rounded-xl text-left px-4 py-3 font-semibold transition-colors ${
                                selectedCategory === ""
                                    ? "bg-[#F1DFC4] text-[#EF5C19]"
                                    : "bg-[#ECECEE] text-[#424650]"
                            }`}
                        >
                            All Products
                        </button>
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => onSelectCategory(category)}
                                className={`w-full rounded-xl text-left px-4 py-3 font-semibold transition-colors ${
                                    selectedCategory === category
                                        ? "bg-[#F1DFC4] text-[#EF5C19]"
                                        : "bg-[#ECECEE] text-[#424650]"
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </section>

                <section>
                    <h4 className="text-[#21232A] text-3xl md:text-xl font-semibold mb-3">Colors</h4>
                    <div className="grid grid-cols-4 gap-3">
                        {colors.map((color) => {
                            const isSelected = selectedColors.includes(color.name);
                            return (
                                <button
                                    key={color.name}
                                    onClick={() => onToggleColor(color.name)}
                                    className={`w-10 h-10 rounded-full border-2 transition-transform ${
                                        isSelected ? "border-[#111217] scale-105" : "border-[#D1D2D7]"
                                    }`}
                                    style={{ backgroundColor: color.hex }}
                                    aria-label={`Filter color ${color.name}`}
                                />
                            );
                        })}
                    </div>
                </section>

                <section>
                    <h4 className="text-[#21232A] text-3xl md:text-xl font-semibold mb-3">Sizes</h4>
                    <div className="grid grid-cols-3 gap-2">
                        {sizes.map((size) => {
                            const isSelected = selectedSizes.includes(size);
                            return (
                                <button
                                    key={size}
                                    onClick={() => onToggleSize(size)}
                                    className={`rounded-xl py-2 font-semibold border transition-colors ${
                                        isSelected
                                            ? "bg-[#111217] text-white border-[#111217]"
                                            : "bg-[#ECECEE] text-[#424650] border-[#ECECEE]"
                                    }`}
                                >
                                    {size}
                                </button>
                            );
                        })}
                    </div>
                </section>

                <section>
                    <h4 className="text-[#21232A] text-3xl md:text-xl font-semibold mb-3">Price Range</h4>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm text-[#666B77] font-semibold">Min</label>
                            <input
                                value={minPrice}
                                onChange={(e) => onMinPriceChange(e.target.value)}
                                type="number"
                                className="w-full mt-1 bg-[#ECECEE] border border-[#D2D4DA] rounded-xl px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-[#666B77] font-semibold">Max</label>
                            <input
                                value={maxPrice}
                                onChange={(e) => onMaxPriceChange(e.target.value)}
                                type="number"
                                className="w-full mt-1 bg-[#ECECEE] border border-[#D2D4DA] rounded-xl px-3 py-2"
                            />
                        </div>
                    </div>
                    <p className="text-center text-[#5D6270] font-semibold mt-4">
                        ${minPrice || "0"} - ${maxPrice || "0"}
                    </p>
                </section>
            </div>
        </aside>
    );
};

const ITEMS_PER_PAGE = 8;

export function Products() {
    const [currentPage, setCurrentPage] = useState(0);
    const { products } = useProducts(false, currentPage + 1, ITEMS_PER_PAGE);
    const allProducts = products?.results ?? [];
    const totalPages = products?.total_pages ?? 1;

    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState<SortOption>("newest");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedColors, setSelectedColors] = useState<string[]>([]);
    const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
    const [minPrice, setMinPrice] = useState("0");
    const [maxPrice, setMaxPrice] = useState("200");
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm, selectedCategory, selectedColors, selectedSizes, minPrice, maxPrice, sortBy]);

    const categories = useMemo(
        () =>
            Array.from(
                new Set(allProducts.map((product) => product.category?.name).filter(Boolean)),
            ) as string[],
        [allProducts],
    );

    const colors = useMemo(() => {
        const map = new Map<string, string>();
        allProducts.forEach((product) => {
            (product.colors ?? []).forEach((color) => {
                if (!map.has(color.name)) {
                    map.set(color.name, color.color || "#E5E7EB");
                }
            });
        });

        return Array.from(map.entries()).map(([name, hex]) => ({ name, hex }));
    }, [allProducts]);

    const sizes = useMemo(() => {
        return Array.from(
            new Set(
                allProducts
                    .flatMap((product) => product.sizes ?? [])
                    .map((size) => size.name)
                    .filter(Boolean),
            ),
        ) as string[];
    }, [allProducts]);

    const filteredProducts = useMemo(() => {
        const min = Number(minPrice || 0);
        const max = Number(maxPrice || Number.MAX_SAFE_INTEGER);

        return allProducts.filter((product: Product) => {
            const matchesSearch =
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.description?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesCategory =
                selectedCategory === "" || product.category?.name === selectedCategory;

            const productColorNames = (product.colors ?? []).map((color) => color.name);
            const matchesColor =
                selectedColors.length === 0 ||
                selectedColors.some((color) => productColorNames.includes(color));

            const productSizeNames = (product.sizes ?? []).map((size) => size.name);
            const matchesSize =
                selectedSizes.length === 0 ||
                selectedSizes.some((size) => productSizeNames.includes(size));

            const matchesPrice = product.price >= min && product.price <= max;

            return (
                matchesSearch &&
                matchesCategory &&
                matchesColor &&
                matchesSize &&
                matchesPrice
            );
        });
    }, [
        allProducts,
        maxPrice,
        minPrice,
        searchTerm,
        selectedCategory,
        selectedColors,
        selectedSizes,
    ]);

    const sortedProducts = useMemo(() => {
        const copy = [...filteredProducts];

        if (sortBy === "price-low") {
            return copy.sort((a, b) => a.price - b.price);
        }

        if (sortBy === "price-high") {
            return copy.sort((a, b) => b.price - a.price);
        }

        if (sortBy === "name") {
            return copy.sort((a, b) => a.name.localeCompare(b.name));
        }

        return copy.sort((a, b) => Number(Boolean(b.new)) - Number(Boolean(a.new)));
    }, [filteredProducts, sortBy]);

    const handleToggleColor = (color: string) => {
        setSelectedColors((prev) =>
            prev.includes(color) ? prev.filter((item) => item !== color) : [...prev, color],
        );
    };

    const handleToggleSize = (size: string) => {
        setSelectedSizes((prev) =>
            prev.includes(size) ? prev.filter((item) => item !== size) : [...prev, size],
        );
    };

    const clearFilters = () => {
        setSelectedCategory("");
        setSelectedColors([]);
        setSelectedSizes([]);
        setMinPrice("0");
        setMaxPrice("200");
        setSearchTerm("");
        setSortBy("newest");
    };

    return (
        <div className="min-h-screen bg-[#E7E7EA]">
            <section className="bg-gradient-to-r from-[#171821] via-[#222430] to-[#1A1C26] border-b border-[#2A2D39] py-10 md:py-16">
                <div className="max-w-[100rem] mx-auto px-4 text-center">
                    <h1 className="text-5xl md:text-7xl font-bold text-white">All Products</h1>
                    <p className="text-[#C3C6D0] mt-3 text-xl md:text-2xl">Discover our complete collection</p>
                </div>
            </section>

            <section className="max-w-[100rem] mx-auto px-4 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
                    <div className="relative">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9498A5]" />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full rounded-2xl bg-[#ECECEF] border border-[#D5D7DE] pl-11 pr-4 py-3 text-[#20232B]"
                        />
                    </div>

                    <div className="relative">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            className="w-full appearance-none rounded-2xl bg-[#ECECEF] border border-[#D5D7DE] px-4 py-3 text-[#20232B]"
                        >
                            <option value="newest">Newest</option>
                            <option value="price-low">Price: Low to High</option>
                            <option value="price-high">Price: High to Low</option>
                            <option value="name">Name</option>
                        </select>
                        <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9498A5] pointer-events-none" />
                    </div>
                </div>

                <button
                    onClick={() => setShowMobileFilters((prev) => !prev)}
                    className="mt-4 w-full lg:hidden rounded-2xl bg-[#1A1B23] text-white font-semibold py-3 flex items-center justify-center gap-2"
                >
                    <FiFilter />
                    Filters
                </button>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
                    <div className="hidden lg:block sticky top-6">
                        <ProductsFilterPanel
                            categories={categories}
                            selectedCategory={selectedCategory}
                            onSelectCategory={setSelectedCategory}
                            colors={colors}
                            selectedColors={selectedColors}
                            onToggleColor={handleToggleColor}
                            sizes={sizes}
                            selectedSizes={selectedSizes}
                            onToggleSize={handleToggleSize}
                            minPrice={minPrice}
                            maxPrice={maxPrice}
                            onMinPriceChange={setMinPrice}
                            onMaxPriceChange={setMaxPrice}
                            onClear={clearFilters}
                        />
                    </div>

                    <div>
                        {showMobileFilters && (
                            <div className="fixed inset-0 z-50 lg:hidden">
                                <button
                                    onClick={() => setShowMobileFilters(false)}
                                    className="absolute inset-0 bg-black/35"
                                    aria-label="Close filters"
                                />

                                <aside className="relative h-full w-[320px] md:w-[360px] max-w-[88vw] bg-[#F4F4F5] border-r border-[#D4D4D8] p-4 overflow-y-auto shadow-2xl">
                                    <div className="flex items-center justify-between border-b border-[#DCDDDF] pb-4 mb-5">
                                        <div className="flex items-center gap-2">
                                            <FiSliders className="text-lg text-[#22232A]" />
                                            <h3 className="text-3xl font-semibold text-[#21232A]">Filters</h3>
                                        </div>
                                        <button
                                            onClick={() => setShowMobileFilters(false)}
                                            className="text-[#3F3F46] hover:text-black transition-colors"
                                            aria-label="Close"
                                        >
                                            <FiX className="text-2xl" />
                                        </button>
                                    </div>

                                    <ProductsFilterPanel
                                        categories={categories}
                                        selectedCategory={selectedCategory}
                                        onSelectCategory={setSelectedCategory}
                                        colors={colors}
                                        selectedColors={selectedColors}
                                        onToggleColor={handleToggleColor}
                                        sizes={sizes}
                                        selectedSizes={selectedSizes}
                                        onToggleSize={handleToggleSize}
                                        minPrice={minPrice}
                                        maxPrice={maxPrice}
                                        onMinPriceChange={setMinPrice}
                                        onMaxPriceChange={setMaxPrice}
                                        onClear={clearFilters}
                                    />

                                    <button
                                        onClick={() => setShowMobileFilters(false)}
                                        className="w-full mt-6 rounded-full bg-[#FB2C36] text-white font-semibold py-3 hover:bg-[#E11D2D] transition-colors"
                                    >
                                        Apply Filters
                                    </button>
                                </aside>
                            </div>
                        )}

                        <p className="text-[#5E636E] text-xl md:text-lg font-semibold mb-2">
                            Showing {sortedProducts.length} of {products?.count ?? 0} results
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8">
                            {sortedProducts.map((product: Product, index: number) => (
                                <Card
                                    key={product.id ?? index}
                                    id={product.id!}
                                    name={product.name}
                                    imageUrl={product.image!}
                                    imageUrls={product.images ?? []}
                                    price={product.price}
                                    phrases={product.phrases}
                                    description={product.description}
                                />
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="flex justify-center">
                                <ReactPaginate
                                    breakLabel="..."
                                    nextLabel={<ButtonRound text="siguiente >" onClick={() => {}} isSelected={false} />}
                                    previousLabel={<ButtonRound text="< anterior" onClick={() => {}} isSelected={false} />}
                                    onPageChange={({ selected }) => setCurrentPage(selected)}
                                    pageCount={totalPages}
                                    forcePage={currentPage}
                                    pageRangeDisplayed={5}
                                    renderOnZeroPageCount={null}
                                    containerClassName="flex items-center"
                                    pageClassName="px-3 py-1"
                                    activeClassName="bg-blue-500 text-white rounded"
                                    previousClassName="px-3 py-1"
                                    nextClassName="px-3 py-1"
                                    disabledClassName="opacity-50 cursor-not-allowed"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}